import * as React from 'react';
import { Attachment } from '../../../../../entities/Subentities';
import { ItemCreation } from '../../../ItemCreation';
import LabellableComponent from '../../../LabellableComponent';
import { notUndefined } from '../../../../utils/common';
import { ImagePicker } from './ImagePicker';
import { ISettings } from './types';
import { MessageBar, MessageBarType } from 'office-ui-fabric-react';
import { SpinButtonInput } from '../../../inputs/SpinButtonInput';

type Props = {
    readOnly?: boolean;
    onDismiss: () => void;
    images: Attachment[];
    value: ISettings;
    onComplete: (value: ISettings) => void;
};

const MIN_HEIGHT = 0;
const MAX_HEIGHT = 2048;

export const ConfigurePanel = ({ readOnly, onDismiss, images, onComplete, value }: Props) => {
    const [isDirty, setIsDirty] = React.useState(false);
    const [contentHeight, setContentHeight] = React.useState(value.contentHeight);
    const [imagesIds, setImagesIds] = React.useState<string[]>(value.imagesIds);

    const save = () => {
        onComplete({ contentHeight, imagesIds });
        onDismiss();
    };

    return (
        <ItemCreation
            isDirty={isDirty}
            header={{
                text: "Configure Images",
                secondaryText: "Select one or more images to display in the Images section.",
                showNameEditor: false
            }}
            commands={[
                readOnly ? undefined : {
                    primary: true,
                    text: "Save",
                    onClick: save
                },
                {
                    text: readOnly ? 'Close' : 'Cancel',
                    onClick: onDismiss
                }
            ].filter(notUndefined)}
            onDismiss={onDismiss}
        >
            <MessageBar messageBarType={MessageBarType.info}>
                Images are taken from the current project only. Please note that custom views are shared across all projects, so images selected in one project will not appear in others. To display images per project, use the Images section in the Default view.
            </MessageBar>
            <div className="panel-area">
                <div className="grid-item">
                    <LabellableComponent
                        label="Images"
                        description="Select one or several attachments of the Image type from the current project attachments."
                        className={`field-container ${readOnly ? "readonly" : ""}`}
                    >
                        <div className={`field-value ${readOnly ? "readonly" : ""}`}>
                            <ImagePicker
                                readOnly={readOnly}
                                selectedIds={imagesIds}
                                images={images}
                                onChange={_ => { setIsDirty(true); setImagesIds(_); }}
                            />
                        </div>
                    </LabellableComponent>
                </div>
                <div className="grid-item">
                    <LabellableComponent
                        label="Content Height"
                        description="Specify the height of the Images section."
                        className={`field-container ${readOnly ? "readonly" : ""}`}
                    >
                        <div className={`field-value ${readOnly ? "readonly" : ""}`}>
                            <SpinButtonInput
                                disabled={readOnly}
                                numberValue={contentHeight}
                                suffix=" px"
                                min={MIN_HEIGHT}
                                max={MAX_HEIGHT}
                                onEditComplete={(_: number) => { setIsDirty(true); setContentHeight(_); }}
                            />
                        </div>
                    </LabellableComponent>
                </div>
            </div>
        </ItemCreation >
    );
};